import { useState, useMemo } from "react";
import { ELEMENTS as elements } from "../data/periodicTable.js";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const EM = "#F8B84E", EM_DEEP = "#7C3AED", EM_LIGHT = "#FCD9A0";
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

// symbol -> atomic mass lookup, built once from the bundled dataset.
const MASS = {};
for (const e of elements) MASS[e.symbol] = typeof e.mass === "number" ? e.mass : parseFloat(e.mass);

/**
 * Parse a chemical formula into element counts.
 * Handles: multi-letter symbols (Ca, Cl), counts (O2), nested parens
 * (Ca(OH)2, K4[Fe(CN)6]), and hydrate dot notation (CuSO4·5H2O / CuSO4.5H2O).
 * Returns { counts: {El: n}, error: string|null }.
 */
function parseFormula(raw) {
  if (!raw || !raw.trim()) return { counts: null, error: null };
  // Normalise hydrate separators to a '+' we handle by splitting.
  const formula = raw.replace(/[·•∙*]/g, ".").trim();

  // Split on hydrate dots at top level (e.g. CuSO4.5H2O), but not decimals.
  // A hydrate dot is a '.' followed by an optional integer then a capital.
  const segments = [];
  let buf = "";
  for (let i = 0; i < formula.length; i++) {
    const ch = formula[i];
    if (ch === "." && /[0-9A-Z]/.test(formula[i + 1] || "")) {
      segments.push(buf); buf = "";
    } else buf += ch;
  }
  segments.push(buf);

  const total = {};
  for (let seg of segments) {
    seg = seg.trim();
    if (!seg) continue;
    // Leading multiplier on a hydrate segment, e.g. "5H2O"
    let mult = 1;
    const m = seg.match(/^(\d+)(?=[A-Z(\[])/);
    if (m) { mult = parseInt(m[1]); seg = seg.slice(m[1].length); }
    const r = parseSegment(seg);
    if (r.error) return { counts: null, error: r.error };
    for (const [el, n] of Object.entries(r.counts)) total[el] = (total[el] || 0) + n * mult;
  }
  return { counts: total, error: null };
}

function parseSegment(s) {
  const stack = [{}];
  const brackets = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    if (ch === "(" || ch === "[") { stack.push({}); brackets.push(ch); i++; continue; }
    if (ch === ")" || ch === "]") {
      i++;
      let num = "";
      while (i < s.length && /\d/.test(s[i])) num += s[i++];
      const mult = num ? parseInt(num) : 1;
      if (!brackets.length) return { counts: null, error: "Unbalanced parentheses" };
      const open = brackets.pop();
      if ((open === "(" && ch !== ")") || (open === "[" && ch !== "]"))
        return { counts: null, error: "Mismatched bracket types" };
      const top = stack.pop();
      if (!stack.length) return { counts: null, error: "Unbalanced parentheses" };
      const into = stack[stack.length - 1];
      for (const [el, n] of Object.entries(top)) into[el] = (into[el] || 0) + n * mult;
      continue;
    }
    const sym = s.slice(i).match(/^[A-Z][a-z]?/);
    if (!sym) return { counts: null, error: `Unexpected character "${ch}"` };
    const el = sym[0];
    if (!(el in MASS)) return { counts: null, error: `Unknown element "${el}"` };
    i += el.length;
    let num = "";
    while (i < s.length && /\d/.test(s[i])) num += s[i++];
    const cnt = num ? parseInt(num) : 1;
    const top = stack[stack.length - 1];
    top[el] = (top[el] || 0) + cnt;
  }
  if (stack.length !== 1 || brackets.length) return { counts: null, error: "Unbalanced parentheses" };
  return { counts: stack[0], error: null };
}

function computeMass(counts) {
  let total = 0;
  const breakdown = [];
  for (const [el, n] of Object.entries(counts)) {
    const m = MASS[el];
    total += m * n;
    breakdown.push({ el, n, m, sub: m * n });
  }
  breakdown.sort((a, b) => b.sub - a.sub);
  return { total, breakdown };
}

export default function MolecularWeight() {
  const [input, setInput] = useState("");
  const result = useMemo(() => {
    const { counts, error } = parseFormula(input);
    if (error) return { error };
    if (!counts || !Object.keys(counts).length) return null;
    return { ...computeMass(counts), counts };
  }, [input]);

  const examples = ["H2O", "C6H12O6", "CaCO3", "Ca(OH)2", "CuSO4·5H2O", "K4[Fe(CN)6]"];

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM,0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(EM,0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #A7F3D0)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Molecular Weight</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 620, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center", margin: "0 0 24px" }}>
          Type a chemical formula to get its molar mass in g/mol. Handles parentheses,
          polyatomic ions, and hydrates (use · or a period, e.g. CuSO4·5H2O).
        </p>

        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. C6H12O6"
          style={{ width: "100%", padding: "16px 18px", borderRadius: 12, border: `1px solid ${rgba(EM,0.25)}`, background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 20, fontFamily: "inherit", outline: "none", boxSizing: "border-box", textAlign: "center", fontWeight: 600 }}
        />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 14 }}>
          {examples.map((ex) => (
            <button key={ex} onClick={() => setInput(ex)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", color: "#9CA3AF", fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>{ex}</button>
          ))}
        </div>

        {result?.error && (
          <div style={{ marginTop: 28, padding: "16px 20px", borderRadius: 12, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", color: "#FDA4AF", fontSize: 14, textAlign: "center" }}>
            {result.error}
          </div>
        )}

        {result && !result.error && (
          <div style={{ marginTop: 28 }}>
            <div style={{ textAlign: "center", padding: "24px", borderRadius: 16, background: rgba(EM,0.07), border: `1px solid ${rgba(EM,0.25)}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Molar mass</div>
              <div style={{ fontSize: 42, fontWeight: 800, color: EM_LIGHT, marginTop: 6 }}>
                {formatNum(result.total, 3)} <span style={{ fontSize: 18, color: "#6B7280" }}>g/mol</span>
              </div>
            </div>

            <div style={{ marginTop: 18, borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr 1.2fr", gap: 0, fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <span>Element</span><span style={{ textAlign: "center" }}>Count</span><span style={{ textAlign: "right" }}>Atomic mass</span><span style={{ textAlign: "right" }}>Subtotal</span>
              </div>
              {result.breakdown.map(({ el, n, m, sub }) => (
                <div key={el} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr 1.2fr", gap: 0, fontSize: 14, color: "#E2E8F0", padding: "11px 16px", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                  <span style={{ fontWeight: 700, color: EM_LIGHT }}>{el}</span>
                  <span style={{ textAlign: "center" }}>{n}</span>
                  <span style={{ textAlign: "right", color: "#9CA3AF" }}>{formatNum(m, 3)}</span>
                  <span style={{ textAlign: "right" }}>{formatNum(sub, 3)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM,0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); input::placeholder { color: #4B5563; } * { box-sizing: border-box; } body { margin: 0; }`}</style>
    </div>
  );
}
